/**
 * themePreview.ts
 *
 * Storefront live-preview bridge (Phase 4).
 *
 * Contract (mirrors the Dashboard Theme Editor right-panel):
 *   window.parent.postMessage({
 *     type: 'DILZEE_THEME_PREVIEW',
 *     config: {
 *       theme_id: string,
 *       cssVars: Record<string, string>,
 *       sectionConfigs?: Record<string, unknown>,
 *       productGridColumns?: number,
 *     }
 *   }, origin)    // Dashboard -> storefront preview iframe
 *
 * Guard (activates ONLY inside the editor's preview iframe):
 *   1. window.top !== window.self
 *   2. URL contains ?preview_token= — a short-lived JWT issued by
 *      GET /stores/preview-token (merchant-authenticated, signed server-side
 *      with the worker's JWT_SECRET). The storefront only checks the token's
 *      shape + expiry here; the signature check happens at issuance time on
 *      the worker, so no signing secret ever ships in the storefront bundle.
 *
 * Messages are accepted only from the trusted editor origin. No network calls,
 * no persistence. The overlay is read-only state.
 */

export interface ThemePreviewConfig {
  theme_id: string;
  cssVars?: Record<string, string>;
  settings?: Record<string, unknown>;
  sectionConfigs?: Record<string, unknown>;
  productGridColumns?: number;
}

const PREVIEW_EVENT = 'DILZEE_THEME_PREVIEW';
const TOKEN_MAX_AGE_SEC = 24 * 60 * 60;

// The theme editor lives inside the dashboard shell. postMessage payloads are
// applied to the local iframe DOM only, but we still refuse foreign origins that
// are not the editor (defence in depth against drive-by theme injection).
const ALLOWED_EDITOR_ORIGINS = new Set([
  'https://dashboard.dilzee.com',
  'https://dilzee.com',
]);

let activeConfig: ThemePreviewConfig | null = null;
const listeners = new Set<(cfg: ThemePreviewConfig | null) => void>();

export function getActiveThemePreview(): ThemePreviewConfig | null {
  return activeConfig;
}

export function subscribeThemePreview(fn: (cfg: ThemePreviewConfig | null) => void): () => void {
  listeners.add(fn);
  fn(activeConfig);
  return () => {
    listeners.delete(fn);
  };
}

function isEmbedded(): boolean {
  return typeof window !== 'undefined' && window.top !== null && window.top !== window.self;
}

function decodeJwtPayload(token: string): Record<string, unknown> | null {
  try {
    const parts = token.split('.');
    if (parts.length !== 3) return null;
    const b64 = parts[1].replace(/-/g, '+').replace(/_/g, '/');
    const padded = b64.padEnd(b64.length + ((4 - (b64.length % 4)) % 4), '=');
    return JSON.parse(atob(padded));
  } catch {
    return null;
  }
}

// The worker mints short-lived preview tokens (24h) server-side after merchant
// auth. Here we only sanity-check shape + expiry — the secret never ships.
function isValidPreviewToken(token: string): boolean {
  const claims = decodeJwtPayload(token);
  if (!claims || typeof claims.exp !== 'number') return false;
  const now = Math.floor(Date.now() / 1000);
  return claims.exp > now && claims.exp - now <= TOKEN_MAX_AGE_SEC;
}

async function isAuthorizedPreview(): Promise<boolean> {
  if (!isEmbedded()) return false;
  const url = new URL(window.location.href);
  const token = url.searchParams.get('preview_token');
  if (!token) return false;
  return isValidPreviewToken(token);
}

function applyCssVars(vars: Record<string, string>): void {
  const root = document.documentElement;
  for (const [k, v] of Object.entries(vars)) {
    root.style.setProperty(k.startsWith('--') ? k : `--${k}`, v);
  }
}

function emit(cfg: ThemePreviewConfig): void {
  activeConfig = cfg;
  for (const fn of listeners) fn(cfg);
}

export async function initThemePreview(): Promise<void> {
  if (!(await isAuthorizedPreview())) return;

  window.addEventListener('message', (event: MessageEvent) => {
    if (!ALLOWED_EDITOR_ORIGINS.has(event.origin)) return;
    try {
      const data = event.data as Record<string, unknown> | undefined;
      if (!data || data.type !== PREVIEW_EVENT) return;

      const config = data.config as ThemePreviewConfig | undefined;
      if (!config || typeof config !== 'object') return;
      
      // Apply CSS variables directly to document root for immediate visual feedback
      if (config.cssVars) applyCssVars(config.cssVars);
      
      // Emit full config so StoreContext and other listeners can react
      emit(config);
    } catch {
      // Malformed preview message: ignore, never crash the storefront.
    }
  });
}